// React
import React from "react";
import { useTranslation } from "react-i18next";
import { validateOfflineEmployeePassword } from "utils/utils";
import { FormBuilder } from "components/FormBuilder/FormBuilder";
import merchantManagement from "routes/merchantManagement";
import MerchantAsyncAutocomplete from "./MerchantAsyncAutocomplete";

const PAGE_LOCALE_KEY = "page.employees.";
const FORM_LABEL_KEY = PAGE_LOCALE_KEY + "employeeDetail.detail.fields";
const TIMEZONE_OPTIONS = (() => {
  return Array.from({ length: 26 }, (_, i) => i - 12) // -12..+13
    .filter((offset) => offset !== 0) // exclude GMT±0
    .map((offset) => {
      const value =
        offset > 0 ? `Etc/GMT-${offset}` : `Etc/GMT+${Math.abs(offset)}`;
      const title = `GMT${offset > 0 ? "+" : ""}${offset}`;
      return { key: value, value, title };
    });
})();

const EmployeeInfo = ({
  formProps,
  isEdit,
  isLoading,
  redirectFn,
  isCreate = false,
  defaultMerchant,
  sectionTitle,
  sectionTitleVariant,
}) => {
  const { t } = useTranslation();

  const isDisabled = !isEdit || isLoading;

  const navigateToMerchantDetail = (merchantId) => {
    const path = "/merchant-management/merchants/offline-merchant-detail";
    const payload = {
      name:
        merchantManagement
          .find((tab) => tab.tabName === "Merchants")
          .page.find((page) => page.path === path).name + merchantId,
      path: path,
      state: {
        merchantId: merchantId,
        redirectFn: redirectFn,
      },
    };
    redirectFn("redirectPage", payload);
  };

  /** @type {Record<string, import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig>} */
  const fieldsConfig = {
    firstName: {
      fieldType: "textInput",
      fieldName: "firstname",
      label: t(`${FORM_LABEL_KEY}.firstName`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 50,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces",
        ]
      }
    },
    lastName: {
      fieldType: "textInput",
      fieldName: "lastname",
      label: t(`${FORM_LABEL_KEY}.lastName`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces"
        ]
      }
    },
    email: {
      fieldType: "textInput",
      fieldName: "email",
      label: t(`${FORM_LABEL_KEY}.email`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        maxLength: 255,
        rules: [
          "noLeadingWhitespace",
          "noQuotesOrBackticks",
          "noChineseCharacters",
          "noFullwidthCharacters",
          "noSpecialSpaces"
        ]
      }
    },
    role: {
      fieldType: "selection",
      fieldName: "role",
      label: t(`${FORM_LABEL_KEY}.role`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        defaultValue: "USER",
        options: ["ADMIN", "USER", "TOP"].map((item) => ({
          key: item,
          title: item,
          value: item,
        })),
      }
    },
    timezone: {
      fieldType: "selection",
      fieldName: "timezone",
      label: t(`${FORM_LABEL_KEY}.timezone`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        defaultValue: "Etc/GMT-8",
        options: TIMEZONE_OPTIONS,
      }
    },
    merchantIdDisplay: {
      fieldType: "textDisplay",
      fieldName: "merchantId",
      label: t(`${FORM_LABEL_KEY}.merchantId`),
      config: {
        onClick: (value) => {
          navigateToMerchantDetail(value);
        }
      }
    },
    permission: {
      fieldType: "selection",
      fieldName: "permission",
      label: t(`${FORM_LABEL_KEY}.permission`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        defaultValue: ["APP_MPOS"],
        options: ["PORTAL", "APP_MPOS"].map((opt) => ({
          key: opt,
          title: opt,
          value: opt,
        })),
        multiSelection: true,
      }
    },
    terminalSerialNumber: {
      fieldType: "textDisplay",
      fieldName: "terminalSerialNumber",
      label: t(`${FORM_LABEL_KEY}.terminalSerialNumber`),
    },
    password: {
      fieldType: "textInput",
      fieldName: "password",
      label: t(`${FORM_LABEL_KEY}.password`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "password",
        customRule: {
          rule: validateOfflineEmployeePassword,
          errorMessage: t(`${PAGE_LOCALE_KEY}createEmployee.passwordStrength`)
        }
      }
    },
    confirmPassword: {
      fieldType: "textInput",
      fieldName: "confirmPassword",
      label: t(`${FORM_LABEL_KEY}.confirmPassword`),
      mandatory: true,
      disabled: isDisabled,
      config: {
        inputType: "password",
        customRule: {
          rule: (value, { password }) => value === password,
          errorMessage: t(`${PAGE_LOCALE_KEY}createEmployee.passwordMismatch`)
        }
      }
    },
  }

  /** @type {import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig[]} */
  const editConfig = [
    fieldsConfig.firstName,
    fieldsConfig.lastName,
    fieldsConfig.email,
    fieldsConfig.role,
    fieldsConfig.timezone,
    fieldsConfig.merchantIdDisplay,
    fieldsConfig.permission,
    fieldsConfig.terminalSerialNumber,
  ]

  /** @type {import("components/FormBuilder/lib/FormBuilderFieldBuilder").formBuilderFieldConfig[]} */
  const createConfig = [
    fieldsConfig.firstName,
    fieldsConfig.lastName,
    fieldsConfig.email,
    fieldsConfig.password,
    fieldsConfig.confirmPassword,
    fieldsConfig.role,
    fieldsConfig.timezone,
    fieldsConfig.permission,
  ]

  const merchantSearchField = isCreate ? (
    <MerchantAsyncAutocomplete
      formProps={formProps}
      defaultMerchant={defaultMerchant}
      disabled={isDisabled}
      label={t(`${FORM_LABEL_KEY}.merchantId`)}
      placeholder={t(`${PAGE_LOCALE_KEY}merchantSearchPlaceholder`)}
      errorMessage={t("general.message.mandatory")}
    />
  ) : null;

  return (
    <FormBuilder.Section
      title={sectionTitle}
      titleVariant={sectionTitleVariant}
    >
      <FormBuilder.Row itemsPerRow={3}>
        {FormBuilder.buildFields({
          formProps,
          fields: isCreate ? createConfig : editConfig,
        })}
        {merchantSearchField}
      </FormBuilder.Row>
    </FormBuilder.Section>
  );
};

export default React.memo(EmployeeInfo);
